import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';

import { WidgetComponent } from '../widget/widget.component';
import { DashboardHeaderComponent } from '../components/dashboard-header/dashboard-header.component';
import { Dashboard, FlexDashboard, Widget } from '../models/dashboard';

type FlexColumn = 'left' | 'right';
type WidgetHeightMode = 'auto' | 'fixed' | 'fill';

export interface FlexDashboardWidgetLayout {
  widgetId: string | number;
  data: Widget;
  column: FlexColumn;
  heightMode: WidgetHeightMode;
  heightPx?: number;
  minHeightPx?: number;
  order?: number;
}

@Component({
  selector: 'app-flex-dashboard',
  standalone: true,
  imports: [CommonModule, WidgetComponent, DashboardHeaderComponent],
  templateUrl: './flex-dashboard.component.html',
  styleUrl: './flex-dashboard.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexDashboardComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);

  dashboard$!: Observable<FlexDashboard | Dashboard>;

  readonly dashboardData = signal<FlexDashboard | Dashboard | null>(null);

  readonly layoutMode = computed<'two-column' | 'single-column'>(() => {
    const d = this.dashboardData() as FlexDashboard | null;
    return d?.layoutMode === 'single-column' ? 'single-column' : 'two-column';
  });

  readonly layoutWidgets = computed<FlexDashboardWidgetLayout[]>(() => {
    const dashboard = this.dashboardData();
    const widgets = dashboard?.widgets ?? [];

    const mapped: FlexDashboardWidgetLayout[] = widgets.map((w: any, index: number) => {
      if (w.data) return w as FlexDashboardWidgetLayout;

      const isLeft = (w.columnPosition ?? 1) <= 3;
      const rowSpan = Math.max(1, Number(w.rowSpan ?? 2));
      const defaultHeightMode: WidgetHeightMode = rowSpan <= 1 ? 'auto' : 'fixed';

      return {
        widgetId: w.widgetId ?? index,
        data: w,
        column: isLeft ? 'left' : 'right',
        heightMode: defaultHeightMode,
        heightPx: defaultHeightMode === 'fixed' ? rowSpan * 130 : undefined,
        minHeightPx: rowSpan <= 1 ? 120 : 180,
        order: Number(w.rowPosition ?? index),
      };
    });

    return mapped;
  });

  readonly leftColumnWidgets = computed(() =>
    this.layoutWidgets()
      .filter(w => w.column === 'left')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  );

  readonly rightColumnWidgets = computed(() =>
    this.layoutWidgets()
      .filter(w => w.column === 'right')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  );

  readonly singleColumnWidgets = computed(() =>
    this.layoutWidgets().slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  );

  ngOnInit(): void {
    this.route.data.subscribe(data => {
      const dashboard = data['dashboard'] as FlexDashboard | Dashboard;
      this.dashboard$ = of(dashboard);
      this.dashboardData.set(dashboard);
    });
  }

  trackByWidgetId = (_: number, item: FlexDashboardWidgetLayout) => item.widgetId;
}
